// Insurance carrier vocabulary for Q9 / in-app Part B.
//
// ⚠️ KEEP IN SYNC with the API's util/providers/normalizeInsurance.js — same canonical
// carriers + same map. This is the patient-facing half; the server folds provider records
// to the same names so `/public/providers` ranking (an exact CI compare) matches.
//
// WHY a curated canonical list, not the raw ~900 US carriers or just the Airtable values:
// the funnel's only use of insurance is to rank PTs a patient can actually use. We don't do
// eligibility here (deferred to LatePoint at booking), so the long tail adds friction with
// zero matching benefit. A dozen consumer-recognizable carriers cover the vast majority of
// insured lives; everything else is "Other" free text. The map was built from the live PT
// Airtable "Accepted Insurance" values (providers write parent brands — "UnitedHealth",
// "Anthem", "Health Net", "AARP" — which fold to the consumer name a patient would pick).

// Consumer-recognizable canonical carriers, ordered largest-first (commercial, then govt).
// Health Net is included because our provider base is CA-heavy and patients hold its cards.
export const INSURANCE_CARRIERS: string[] = [
  'UnitedHealthcare',
  'Blue Cross Blue Shield',
  'Aetna',
  'Cigna',
  'Kaiser Permanente',
  'Humana',
  'Health Net',
  'Medicare',
  'Medicaid',
  'Tricare',
];

// The always-present escape hatch. Selecting it reveals a free-text field so the patient
// can type a carrier we don't list — captured verbatim as insuranceProvider.
export const INSURANCE_OTHER = 'Other / not listed';

// UK private medical insurers (PMI), largest-first. Shown to GB patients instead of the US
// carriers above — a UK physio is paid via the NHS (free) or privately (self-pay or PMI), so
// these are the recognizable PMI brands a patient would hold a policy with. Kept short for
// the same reason as the US list: this only helps rank/route, not eligibility. NOTE: the
// API's insurance ranking (util/providers/normalizeInsurance.js) only knows US carriers
// today; UK matching is concierge, so these are captured for the concierge, not yet ranked.
export const UK_INSURANCE_PLANS: string[] = [
  'Bupa',
  'AXA Health',
  'Vitality',
  'Aviva',
  'WPA',
  'Healix',
];

// normalized raw value -> canonical name. Mirror of the API map.
const MAP: Record<string, string> = {
  unitedhealthcare: 'UnitedHealthcare',
  unitedhealth: 'UnitedHealthcare',
  'united healthcare': 'UnitedHealthcare',
  'united health': 'UnitedHealthcare',
  uhc: 'UnitedHealthcare',
  optum: 'UnitedHealthcare',
  aarp: 'UnitedHealthcare',
  'blue cross blue shield': 'Blue Cross Blue Shield',
  bcbs: 'Blue Cross Blue Shield',
  'blue cross': 'Blue Cross Blue Shield',
  'blue shield': 'Blue Cross Blue Shield',
  anthem: 'Blue Cross Blue Shield',
  elevance: 'Blue Cross Blue Shield',
  'elevance health': 'Blue Cross Blue Shield',
  carefirst: 'Blue Cross Blue Shield',
  'california physicians service': 'Blue Cross Blue Shield',
  guidewell: 'Blue Cross Blue Shield',
  'health care services corporation hcsc': 'Blue Cross Blue Shield',
  hcsc: 'Blue Cross Blue Shield',
  'independence health group': 'Blue Cross Blue Shield',
  highmark: 'Blue Cross Blue Shield',
  wellpoint: 'Blue Cross Blue Shield',
  aetna: 'Aetna',
  cvs: 'Aetna',
  cigna: 'Cigna',
  evernorth: 'Cigna',
  'kaiser permanente': 'Kaiser Permanente',
  kaiser: 'Kaiser Permanente',
  humana: 'Humana',
  'health net': 'Health Net',
  healthnet: 'Health Net',
  medicare: 'Medicare',
  wellcare: 'Medicare',
  medicaid: 'Medicaid',
  'medi cal': 'Medicaid',
  amerigroup: 'Medicaid',
  caresource: 'Medicaid',
  centene: 'Medicaid',
  'centene corp': 'Medicaid',
  molina: 'Medicaid',
  'molina healthcare': 'Medicaid',
  mcna: 'Medicaid',
  'mcna insurance co': 'Medicaid',
  tricare: 'Tricare',
  'tricare east': 'Tricare',
  'tricare west': 'Tricare',
  champva: 'Tricare',
};

function normalizeKey(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[().,/\\-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Fold a raw carrier string to its canonical name, or null if unrecognized.
export function normalizeInsurance(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const key = normalizeKey(raw);
  return key ? MAP[key] ?? null : null;
}
